import eventBus from './EventBus.js'
import { post } from 'aws-amplify/api'
import { getUserSession, setUserId } from '@/service/UserService.js'
import { randomString } from '../../../api/common/sharedUtils.js'
import { getUserTz } from '@/service/uiUtils.js'

class AuthRequiredError extends Error {
    constructor(message = 'no token') {
        super(message)
        this.name = 'AuthRequiredError'
    }
}

// Client-side cache for dashboard view results. Dashboard components re-fetch on
// every mount / tag change; without this, switching views or drilling in and out
// re-hits the API (and shows a loading spinner) even though the underlying result
// is unchanged. Entries expire after the TTL so data stays reasonably fresh, and
// `forceRegenerate` always bypasses and refreshes the cached entry.
const VIEW_RESULTS_CACHE_TTL_MS = 12 * 60 * 60 * 1000

class Api {
    constructor() {
        this._viewResultsCache = new Map()
    }

    _getCachedViewResults(key) {
        const entry = this._viewResultsCache.get(key)
        if (!entry) return null
        if (Date.now() - entry.at > VIEW_RESULTS_CACHE_TTL_MS) {
            this._viewResultsCache.delete(key)
            return null
        }
        return entry.value
    }

    _setCachedViewResults(key, value) {
        this._viewResultsCache.set(key, { at: Date.now(), value })
    }

    // Drop cached dashboard results. Call with no args to clear everything, or pass
    // an orgId to clear just that org's entries (e.g. after data changes).
    clearViewResultsCache({ orgId } = {}) {
        if (!orgId) {
            this._viewResultsCache.clear()
            return
        }
        for (const key of this._viewResultsCache.keys()) {
            if (key.includes(`:${orgId}:`)) this._viewResultsCache.delete(key)
        }
    }

    async doRequest({ body, path = '/' }) {
        const { token } = await getUserSession()
        if (!token) throw new AuthRequiredError()

        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        }
        if (process.env.KN_API_ENDPOINT === 'http://localhost:3333') {
            const url = process.env.KN_API_ENDPOINT + path
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
            })
            const userId = response.headers.get('x-kn-userid')
            if (userId) setUserId({ userId })
            if (!response.ok) {
                let text = `HTTP error! status: ${response.status}`
                if (response.status === 402) {
                    text = 'You have exceeded your usage limit for this feature. ' +
                        'Please navigate to billing to check current usage and consider upgrading!'
                }
                throw new Error(text)
            }
            return await response.json()
        }
        try {
            const resp = await post({ apiName: 'kinectin-api', path, options: { headers, body } })
            const response = await resp.response
            const userId = response.headers['x-kn-userid']
            if (userId) setUserId({ userId })
            return (await response.body).json()
        } catch (err) {
            const status = err.response?.statusCode
            if (status) {
                let text = `HTTP error! status: ${status}`
                if (status === 402) {
                    text = 'You have exceeded your usage limit for this feature. ' +
                        'Please navigate to billing to check current usage and consider upgrading!'
                }
                throw new Error(text)
            }
            throw err
        }
    }

    async getOpenThreadMarkers() {
        return this.doRequest({ path: '/getOpenThreadMarkers', body: {} })
    }

    async handleSignUp({ orgId } = {}) {
        return this.doRequest({ path: '/handleSignUp', body: { orgId } })
    }

    async getUserProfile() {
        return this.doRequest({ path: '/getUserProfile', body: {} })
    }

    async updateUserProfile({ attrs }) {
        return this.doRequest({ path: '/updateUserProfile', body: { attrs } })
    }

    async getRecentJournalEntries() {
        return this.doRequest({ path: '/getRecentJournalEntries', body: {} })
    }

    async updateJournalEntry({
                                 day,
                                 scores,
                                 wellbeingScore,
                                 distressScore,
                                 journalContent,
                                 empathyResponse,
                                 empathyGeneratedAt,
                             }) {
        return this.doRequest({
            path: '/updateJournalEntry',
            body: { day, scores, wellbeingScore, distressScore, journalContent, empathyResponse, empathyGeneratedAt },
        })
    }

    async getJournalEntryByDay({ day }) {
        return this.doRequest({ path: '/getJournalEntryByDay', body: { day } })
    }

    async getRecentBaselineEntries() {
        return this.doRequest({ path: '/getRecentBaselineEntries', body: {} })
    }

    async updateBaselineEntry({ month, scores, wellbeingScore, distressScore, done }) {
        return this.doRequest({
            path: '/updateBaselineEntry',
            body: { month, scores, wellbeingScore, distressScore, done },
        })
    }

    async createCheckoutSession({ stripeProductId, onboarding }) {
        return this.doRequest({ path: '/createCheckoutSession', body: { stripeProductId, onboarding } })
    }

    async createPortalSession() {
        return this.doRequest({ path: '/createPortalSession', body: {} })
    }

    async getUserRevCatInfo() {
        return this.doRequest({ path: '/getUserRevCatInfo', body: {} })
    }

    async getLedgerLines({ lastLedgerLineId } = {}) {
        return this.doRequest({ path: '/getLedgerLines', body: { lastLedgerLineId } })
    }

    async getTokenUsage() {
        return this.doRequest({ path: '/getTokenUsage', body: {} })
    }

    async getUserPreferences() {
        return this.doRequest({ path: '/getUserPreferences', body: {} })
    }

    async updateUserPreferences({ attrs }) {
        return this.doRequest({ path: '/updateUserPreferences', body: { attrs } })
    }

    async getNotificationPreferences() {
        return this.doRequest({ path: '/getNotificationPreferences', body: {} })
    }

    async updateNotificationPreferences({ attrs }) {
        return this.doRequest({ path: '/updateNotificationPreferences', body: { attrs } })
    }

    async getUserContacts() {
        return this.doRequest({ path: '/getUserContacts', body: {} })
    }

    async updateUserContacts({ attrs }) {
        return this.doRequest({ path: '/updateUserContacts', body: { attrs } })
    }

    async createRelationship({ attrs }) {
        return this.doRequest({ path: '/createRelationship', body: { attrs } })
    }

    async getRelationships() {
        return this.doRequest({ path: '/getRelationships', body: {} })
    }

    async updateRelationship({ relationshipId, attrs }) {
        return this.doRequest({ path: '/updateRelationship', body: { relationshipId, attrs } })
    }

    async getUserSummary() {
        return this.doRequest({ path: '/getUserSummary', body: {} })
    }

    async updateUserSummary({ summary, fromMessages }) {
        return this.doRequest({ path: '/updateUserSummary', body: { summary, fromMessages } })
    }

    async getRelationshipSummary({ relationshipId }) {
        return this.doRequest({ path: '/getRelationshipSummary', body: { relationshipId } })
    }

    async updateRelationshipSummary({ relationshipId, summary }) {
        return this.doRequest({ path: '/updateRelationshipSummary', body: { relationshipId, summary } })
    }

    async streamCoachResponse({ threadId, llmThreadId, coachGreetMsg, prompt, callbackFn }) {
        let i = 0
        const received = []

        function handleSequence({ delta, status, toolOutput, index }) {
            received[index] = { delta, status, toolOutput }
            while (received[i]) {
                callbackFn(received[i])
                i += 1
            }
        }

        const messageType = `streamCoachResponse-${threadId}`
        eventBus.api.addWsHandler({ messageType, fn: handleSequence })
        const wsSessionId = eventBus.getWsSessionId()
        const body = { wsSessionId, threadId, llmThreadId, coachGreetMsg, prompt }
        const res = await this.doRequest({ path: '/streamCoachResponse', body })
        eventBus.api.removeWsHandler({ messageType })
        return res
    }

    async createThread({ attrs }) {
        return this.doRequest({ path: '/createThread', body: { attrs } })
    }

    async updateThread({ threadId, attrs }) {
        return this.doRequest({ path: '/updateThread', body: { threadId, attrs } })
    }

    async closeThread({ threadId }) {
        return this.doRequest({ path: '/closeThread', body: { threadId } })
    }

    async getThread({ threadId }) {
        return this.doRequest({ path: '/getThread', body: { threadId } })
    }

    async getThreads({ lastThreadId, threadIds }) {
        return this.doRequest({ path: '/getThreads', body: { lastThreadId, threadIds } })
    }

    async getOrgProfiles({ lastOrgId }) {
        return this.doRequest({ path: '/getOrgProfiles', body: { lastOrgId } })
    }

    async getOrgProfile({ orgId } = {}) {
        return this.doRequest({ path: '/getOrgProfile', body: { orgId } })
    }

    async getOrgPublicProfile({ orgId } = {}) {
        return this.doRequest({ path: '/getOrgPublicProfile', body: { orgId } })
    }

    async createOrgProfile({ attrs }) {
        return this.doRequest({ path: '/createOrgProfile', body: { attrs } })
    }

    async updateOrgProfile({ orgId, attrs }) {
        return this.doRequest({ path: '/updateOrgProfile', body: { orgId, attrs } })
    }

    async getOrgUsers({ orgId, lastEmail, emailPrefix }) {
        return this.doRequest({ path: '/getOrgUsers', body: { orgId, lastEmail, emailPrefix } })
    }

    async getAllOrgUsers({ orgId }) {
        return this.doRequest({ path: '/getAllOrgUsers', body: { orgId } })
    }

    async createOrgUsers({ orgId, emails, manageOrgId }) {
        return this.doRequest({ path: '/createOrgUsers', body: { orgId, emails, manageOrgId } })
    }

    async resolveOrgUsers({ orgId, userIds }) {
        return this.doRequest({ path: '/resolveOrgUsers', body: { orgId, userIds } })
    }

    async updateOrgUser({ orgId, email, userId, attrs }) {
        return this.doRequest({ path: '/updateOrgUser', body: { orgId, email, userId, attrs } })
    }

    async getOrgDocs({ orgId, lastDocId }) {
        return this.doRequest({ path: '/getOrgDocs', body: { orgId, lastDocId } })
    }

    async getOrgDoc({ orgId, docId }) {
        return this.doRequest({ path: '/getOrgDoc', body: { orgId, docId } })
    }

    async getOrgDocDownloadUrl({ orgId, docId }) {
        return this.doRequest({ path: '/getOrgDocDownloadUrl', body: { orgId, docId } })
    }

    async getOrgDocUploadUrls({ orgId, fileNamesAndTypes }) {
        return this.doRequest({ path: '/getOrgDocUploadUrls', body: { orgId, fileNamesAndTypes } })
    }

    async createOrgDocs({ orgId, docInfoList }) {
        return this.doRequest({ path: '/createOrgDocs', body: { orgId, docInfoList } })
    }

    async deleteOrgDoc({ orgId, docId }) {
        return this.doRequest({ path: '/deleteOrgDoc', body: { orgId, docId } })
    }

    async createOrgReport({ orgId, reportType }) {
        return this.doRequest({ path: '/createOrgReport', body: { orgId, reportType } })
    }

    async getOrgReportDownloadUrl({ orgId, reportId }) {
        return this.doRequest({ path: '/getOrgReportDownloadUrl', body: { orgId, reportId } })
    }

    async getOrgReports({ orgId, lastReportId }) {
        return this.doRequest({ path: '/getOrgReports', body: { orgId, lastReportId } })
    }

    async deleteOrgUser({ orgId, email }) {
        return this.doRequest({ path: '/deleteOrgUser', body: { orgId, email } })
    }

    async createOrgNudge({ orgId, attrs }) {
        return this.doRequest({ path: '/createOrgNudge', body: { orgId, attrs } })
    }

    async updateOrgNudge({ orgId, nudgeId, attrs }) {
        return this.doRequest({ path: '/updateOrgNudge', body: { orgId, nudgeId, attrs } })
    }

    async createOrgNudgeCategory({ orgId, attrs }) {
        return this.doRequest({ path: '/createOrgNudgeCategory', body: { orgId, attrs } })
    }

    async updateOrgNudgeCategory({ orgId, categoryId, attrs }) {
        return this.doRequest({ path: '/updateOrgNudgeCategory', body: { orgId, categoryId, attrs } })
    }

    async getOrgNudgeCategories({ orgId, lastCategoryId }) {
        return this.doRequest({ path: '/getOrgNudgeCategories', body: { orgId, lastCategoryId } })
    }

    async getOrgCategoryNudges({ orgId, categoryId, lastNudgeId }) {
        return this.doRequest({ path: '/getOrgCategoryNudges', body: { orgId, categoryId, lastNudgeId } })
    }

    async getOrgNudges({ orgId, lastNudgeId }) {
        return this.doRequest({ path: '/getOrgNudges', body: { orgId, lastNudgeId } })
    }

    async getOrgNudge({ orgId, nudgeId }) {
        return this.doRequest({ path: '/getOrgNudge', body: { orgId, nudgeId } })
    }

    async createOrgActivity({ orgId, attrs }) {
        return this.doRequest({ path: '/createOrgActivity', body: { orgId, attrs } })
    }

    async updateOrgActivity({ orgId, activityId, attrs }) {
        return this.doRequest({ path: '/updateOrgActivity', body: { orgId, activityId, attrs } })
    }

    async getOrgActivities({ orgId, lastActivityId }) {
        return this.doRequest({ path: '/getOrgActivities', body: { orgId, lastActivityId } })
    }

    async getOrgActivity({ orgId, activityId }) {
        return this.doRequest({ path: '/getOrgActivity', body: { orgId, activityId } })
    }

    async getOrgActivitiesDownloadUrl({ orgId }) {
        return this.doRequest({ path: '/getOrgActivitiesDownloadUrl', body: { orgId } })
    }

    async createOrgScreener({ orgId, attrs }) {
        return this.doRequest({ path: '/createOrgScreener', body: { orgId, attrs } })
    }

    async updateOrgScreener({ orgId, screenerId, attrs }) {
        return this.doRequest({ path: '/updateOrgScreener', body: { orgId, screenerId, attrs } })
    }

    async getOrgScreeners({ orgId, lastScreenerId }) {
        return this.doRequest({ path: '/getOrgScreeners', body: { orgId, lastScreenerId } })
    }

    async getOrgScreener({ orgId, screenerId }) {
        return this.doRequest({ path: '/getOrgScreener', body: { orgId, screenerId } })
    }

    async updateScreenerAnswers({ orgId, screenerId, timeFragment, answers, comments }) {
        return this.doRequest({
            path: '/updateScreenerAnswers',
            body: { orgId, screenerId, timeFragment, answers, comments },
        })
    }

    async getRecentScreenerAnswers({ orgId, screenerId }) {
        return this.doRequest({ path: '/getRecentScreenerAnswers', body: { orgId, screenerId } })
    }

    async getScreenerAnswerByDay({ orgId, screenerId, timeFragment }) {
        return this.doRequest({ path: '/getScreenerAnswerByDay', body: { orgId, screenerId, timeFragment } })
    }

    async getOrgScreenerResults({ orgId, screenerId, questionKey, selectedTags, columnMode }) {
        return this.doRequest({
            path: '/getOrgScreenerResults',
            body: { userTz: getUserTz(), orgId, screenerId, questionKey, selectedTags, columnMode },
        })
    }

    async getOrgViewResults({ orgId, viewId, componentIndex, selectedTag, forceRegenerate }) {
        const cacheKey = `ovr:${orgId}:${viewId}:${componentIndex}:${selectedTag || ''}`
        if (!forceRegenerate) {
            const cached = this._getCachedViewResults(cacheKey)
            if (cached) return structuredClone(cached)
        }
        const result = await this.doRequest({
            path: '/getOrgViewResults',
            body: { userTz: getUserTz(), orgId, viewId, componentIndex, selectedTag, forceRegenerate },
        })
        this._setCachedViewResults(cacheKey, result)
        return structuredClone(result)
    }

    async getStaffViewResults({ orgId, viewId, componentIndex, forceRegenerate }) {
        const cacheKey = `svr:${orgId}:${viewId ?? ''}:${componentIndex ?? ''}`
        if (!forceRegenerate) {
            const cached = this._getCachedViewResults(cacheKey)
            if (cached) return structuredClone(cached)
        }
        const result = await this.doRequest({
            path: '/getStaffViewResults',
            body: { userTz: getUserTz(), orgId, viewId, componentIndex, forceRegenerate },
        })
        this._setCachedViewResults(cacheKey, result)
        return structuredClone(result)
    }

    async getPulseFactorInsights({ orgId, viewId, componentIndex, factorType, factorValue, selectedTag, forceRegenerate, updatesFn }) {
        const messageType = `getPulseFactorInsights-${randomString(5)}`
        const messageTypeUpdates = `${messageType}-updates` // convention

        return new Promise(async (resolve, reject) => {
            eventBus.api.addWsHandler({ messageType: messageTypeUpdates, fn: updatesFn })
            eventBus.api.addWsHandler({
                messageType,
                fn: async ({ response, error }) => {
                    try {
                        resolve({ response, error })
                    } catch (err) {
                        reject({ error: err })
                    } finally {
                        eventBus.api.removeWsHandler({ messageType })
                        eventBus.api.removeWsHandler({ messageType: messageTypeUpdates })
                    }
                },
            })
            await this.doRequest({
                path: '/getPulseFactorInsights',
                body: {
                    userTz: getUserTz(),
                    wsSessionId: eventBus.getWsSessionId(),
                    messageType,
                    orgId,
                    viewId,
                    componentIndex,
                    factorType,
                    factorValue,
                    selectedTag,
                    forceRegenerate,
                },
            })
        })
    }

    async createOrgView({ orgId, attrs }) {
        return this.doRequest({ path: '/createOrgView', body: { orgId, attrs } })
    }

    async updateOrgView({ orgId, viewId, attrs }) {
        return this.doRequest({ path: '/updateOrgView', body: { orgId, viewId, attrs } })
    }

    async getOrgViews({ orgId, lastViewId }) {
        return this.doRequest({ path: '/getOrgViews', body: { orgId, lastViewId } })
    }

    async getOrgView({ orgId, viewId }) {
        return this.doRequest({ path: '/getOrgView', body: { orgId, viewId } })
    }

    async userFollowsOrg({ orgId }) {
        return this.doRequest({ path: '/userFollowsOrg', body: { orgId } })
    }

    async getFollowedOrgs() {
        return this.doRequest({ path: '/getFollowedOrgs', body: {} })
    }

    async getRelationshipThreads({ relationshipId, lastThreadId, threadIds }) {
        return this.doRequest({ path: '/getRelationshipThreads', body: { relationshipId, lastThreadId, threadIds } })
    }

    async getMessages({ threadId, lastMessageId }) {
        return this.doRequest({ path: '/getMessages', body: { threadId, lastMessageId } })
    }

    async deleteThread({ threadId, llmThreadId }) {
        return this.doRequest({ path: '/deleteThread', body: { threadId, llmThreadId } })
    }

    async deleteMessages({ threadId, llmThreadId, fromMessageId, fromLlmMessageId }) {
        return this.doRequest({
            path: '/deleteMessages',
            body: { threadId, llmThreadId, fromMessageId, fromLlmMessageId },
        })
    }

    async generateSuggestions({ messages, threadId }) {
        return this.doRequest({ path: '/generateSuggestions', body: { messages, threadId } })
    }

    async suggestThreadName({ threadId }) {
        return this.doRequest({ path: '/suggestThreadName', body: { threadId } })
    }

    async getCoachGreet({ firstSession }) {
        return this.doRequest({ path: '/getCoachGreet', body: { firstSession } })
    }

    async getScreenerCompletionGreet({ orgId, screenerId }) {
        return this.doRequest({ path: '/getScreenerCompletionGreet', body: { orgId, screenerId } })
    }

    async submitFeedback({ feedback }) {
        return this.doRequest({ path: '/submitFeedback', body: { feedback } })
    }

    async getProfileImageUploadUrl() {
        return this.doRequest({ path: '/getProfileImageUploadUrl', body: {} })
    }

    async getOrgS3UploadUrl({ orgId, fileName, filePrefix, fileSuffix, contentType, usePrivateBucket }) {
        return this.doRequest({
            path: '/getOrgS3UploadUrl',
            body: { orgId, fileName, filePrefix, fileSuffix, contentType, usePrivateBucket },
        })
    }

    async getOrgTextFile({ orgId, fileName }) {
        return this.doRequest({ path: '/getOrgTextFile', body: { orgId, fileName } })
    }

    async testPushNotification({ heading, content, data }) {
        return this.doRequest({ path: '/testPushNotification', body: { heading, content, data } })
    }

    async deleteAccount() {
        return this.doRequest({ path: '/deleteAccount', body: {} })
    }

    async registerDevice({ deviceToken, platform }) {
        return this.doRequest({ path: '/registerDevice', body: { deviceToken, platform } })
    }

    async logoutNative({ deviceToken }) {
        return this.doRequest({ path: '/logoutNative', body: { deviceToken } })
    }

    async adminGetAllOrgs() {
        return this.doRequest({ path: '/adminGetAllOrgs', body: {} })
    }

    async adminGetAllUsers({ lastEmail, emailPrefix }) {
        return this.doRequest({ path: '/adminGetAllUsers', body: { lastEmail, emailPrefix } })
    }

    async adminUpdateUsers({ users }) {
        return this.doRequest({ path: '/adminUpdateUsers', body: { users } })
    }

    async adminDeleteUser({ email }) {
        return this.doRequest({ path: '/adminDeleteUser', body: { email } })
    }

    async adminProvisionUsers({ emails, initStripeProductId }) {
        return this.doRequest({ path: '/adminProvisionUsers', body: { emails, initStripeProductId } })
    }

    async analyzeBaseline({ scores, monthLabel }) {
        return this.doRequest({ path: '/analyzeBaseline', body: { scores, monthLabel } })
    }

    async updateBaselineAnalysis({ month, analysisResult, analysisVersion }) {
        return this.doRequest({ path: '/updateBaselineAnalysis', body: { month, analysisResult, analysisVersion } })
    }

    async analyzeDailyJournal({ period, startDate, endDate }) {
        return this.doRequest({ path: '/analyzeDailyJournal', body: { period, startDate, endDate } })
    }

    async analyzeDailyCheckinEmpathy({ scores, emojiScore, emojiLabel, period }) {
        return this.doRequest({ path: '/analyzeDailyCheckinEmpathy', body: { scores, emojiScore, emojiLabel, period } })
    }

    async analyzePulseCheckEmpathy({ emojiScore, emojiLabel, period, comments }) {
        return this.doRequest({ path: '/analyzePulseCheckEmpathy', body: { emojiScore, emojiLabel, period, comments } })
    }

    async adminGetSettings() {
        return this.doRequest({ path: '/adminGetSettings', body: {} })
    }

    async adminUpdateSettings({ attrs }) {
        return this.doRequest({ path: '/adminUpdateSettings', body: { attrs } })
    }
}

const api = new Api()
export { AuthRequiredError }
export default api
